import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import ChecklistCampaignBanner from '../components/ChecklistCampaignBanner';
import HeroWithTabs from '../components/HeroWithTabs';
import WhatYoullDiscover from '../components/WhatYoullDiscover';
import MemberEcosystem from '../components/MemberEcosystem';
import HowItWorks from '../components/HowItWorks';
import TrustStrip from '../components/TrustStrip';
import AudienceGateway from '../components/AudienceGateway';
import ApexSixPreview from '../components/ApexSixPreview';
import TestimonialsSection from '../components/TestimonialsSection';
import LatestJournalSection from '../components/LatestJournalSection';
import FinalCTABanner from '../components/FinalCTABanner';
import Footer from '../components/Footer';
import MobileStickyBar from '../components/MobileStickyBar';
import PrivacyPolicy from '../components/PrivacyPolicy';
import { NewsletterStickyBanner } from '../components/NewsletterForm';
import { captureRefFromUrl } from '../lib/growth';
import { CALENDLY_URL, LOGO_URL, INSTAGRAM_URL } from '../lib/siteConfig';

/**
 * Marketing homepage (Phase 22 IA restructure).
 *
 * Reduced from 14 stacked sections to 6. The homepage is now a GATEWAY \u2014
 * every audience-specific section, pricing block, lead magnet, and method
 * deep-dive has been re-homed onto its dedicated landing page:
 *
 *   AudienceSegmentation, AssessmentPromo, SimplifiedPricing, AICoach,
 *   MindsetQuizPromo, LeadMagnetTabs            \u2192 /athletes  (+ /parents, /coaches)
 *   ClubsBand                                   \u2192 /schools
 *   ApexSixMethod (full), FAQSection            \u2192 /method
 *   CredibilitySection                          \u2192 /about (Bio Hub, Phase 21)
 *   ContactSection                              \u2192 footer / Discovery Call CTA
 *
 * The 6 remaining homepage sections are intentionally short \u2014 hero, trust
 * strip, gateway cards (pick your audience), compact Apex Six preview,
 * testimonials, and a single closing CTA banner. Visitors should be 1\u20132
 * scrolls away from a destination, not 12.
 */
export default function MarketingSite() {
  const [privacyOpen, setPrivacyOpen] = useState(false);

  useEffect(() => {
    captureRefFromUrl();
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground pb-[72px] md:pb-0">
      <a href="#main-content" className="skip-link" data-testid="skip-link">
        Skip to main content
      </a>
      <Navbar
        calendlyLink={CALENDLY_URL}
        logoUrl={LOGO_URL}
        topAccessory={
          // "Train the Mind Like a Pro" IG campaign — dismissible promo strip.
          // Lives INSIDE the fixed navbar so the campaign strip + nav stack as
          // one cohesive top bar (no overlap with menu links / CTAs).
          <ChecklistCampaignBanner />
        }
      />
      <main id="main-content">
        {/* 1. Hero — Quiz-first primary CTA, Discovery secondary (Phase 33). */}
        <HeroWithTabs calendlyLink={CALENDLY_URL} />

        {/* 1b. What You'll Discover — quiz teaser block. Sits right under the
            hero so visitors who don't click the primary CTA immediately
            still see exactly what they'll get from the quiz. */}
        <WhatYoullDiscover />

        {/* 2. Trust strip — short credibility bar */}
        <TrustStrip />

        {/* 3. Who We Serve — gateway cards into the 4 audience pages */}
        <AudienceGateway />

        {/* 3b. How it works — general 30-day install promise. Placed directly
            after the audience gateway so cold mixed traffic sees the path
            BEFORE they choose an audience track. */}
        <HowItWorks variant="general" />

        {/* 3c. Member Ecosystem — 4-card ladder (Free → IDP → Locker Room →
            AI Coach). Positions the membership tier explicitly so the homepage
            sells the SYSTEM, not just the quiz. */}
        <MemberEcosystem />

        {/* 4. The Method — compact 6-tile preview + link to full /method */}
        <ApexSixPreview />

        {/* 5. Testimonials */}
        <TestimonialsSection />

        {/* 5b. The Apex Journal — promote recent essays (Phase 27).
            Placed after social proof and before the closing CTA so readers
            who scrolled this far see the brand's editorial voice without it
            crowding the navbar. */}
        <LatestJournalSection />

        {/* 6. Final CTA — Quiz primary, Discovery secondary (mirrors hero). */}
        <FinalCTABanner
          primaryHref="/quiz"
          primaryLabel="Take the Free Mindset Quiz"
          secondaryHref={CALENDLY_URL}
          secondaryLabel="Book a Discovery Call"
        />

        {/* Footer */}
        <Footer
          calendlyLink={CALENDLY_URL}
          instagramLink={INSTAGRAM_URL}
          logoUrl={LOGO_URL}
          onPrivacyClick={() => setPrivacyOpen(true)}
        />
      </main>
      <MobileStickyBar calendlyLink={CALENDLY_URL} />
      <NewsletterStickyBanner />
      <PrivacyPolicy open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
}
